import * as React from 'react'
import styled, { AnyStyledComponent } from 'styled-components'
import moment from 'moment'
import * as constants from '../../../static/constants'
import * as interfaces from '../../../static/interfaces'
import * as Hooks from '../../../utils/hooks'
import { ISelectedGuests } from 'utils/reservation'
import { pushDataLayer } from 'utils/googleTagManager'
import { ReservationDetailInfo } from './parts/ReservationDetailInfo'
import { ReservationResult } from './parts/ReservationResult'
import RefundInfoModal from './parts/RefundInfoModal'
import { RelatedPosts } from 'components/organisms/Posts/Show/RelatedPosts'

import { postService } from '../../../core/services'
import { FullScreenReservationLayout } from '../_layouts/fullscreenReservationLayout'
import {
  ReservationFullScreenModalHeaderContent,
  ReservationModalType,
} from 'components/molecules/Reservation/ReservationFullScreenModalHeader'
import { IJsonResponse } from 'core/JsonApiSerializer'
import { utilService } from 'core/services'
import {
  clearReservationStorageAndInfo,
  getReservationCompletedTransactionId,
  getReservationStorageData,
  setReservationCompletedTransactionId,
} from 'utils/reservationSessionStorage'

interface IWrapProps {
  user: interfaces.IUser
  post: IJsonResponse
  cancel_policy: string
}

interface IProps {
  post: any
  memberPost: any
  guests: ISelectedGuests
  reservation: {
    selectedDate: moment.Moment
    totalPrice: number
    departure: any
  }
  cancelPolicy: string
  guest: interfaces.IUser
  createdReservation: any
  hasCoupon: boolean
  chargePrice: number
}

const WrapReservationThankyou: React.FC<IWrapProps> = props => {
  const { post } = postService.getPostFromJson(props.post)

  const reservationStorageData = getReservationStorageData()

  React.useEffect(() => {
    clearReservationStorageAndInfo()
    setTimeout(() => {
      if (!reservationStorageData?.createdReservation) {
        window.location.href = `/posts/${post.slug}`
      }
    }, 300)
  }, [])

  if (!reservationStorageData?.createdReservation) {
    return null
  }

  return (
    <ReservationThankyou
      {...props}
      post={post}
      guest={props.user}
      cancelPolicy={props.cancel_policy}
      guests={reservationStorageData?.guests}
      memberPost={reservationStorageData?.memberPost}
      reservation={{
        ...reservationStorageData?.reservation,
        selectedDate: moment(reservationStorageData?.reservation?.selectedDate),
        totalPrice: reservationStorageData?.reservation?.totalPrice ?? 0,
        departure: reservationStorageData?.reservation?.departure ?? null,
      }}
      chargePrice={reservationStorageData?.chargePrice}
      hasCoupon={reservationStorageData?.hasCoupon}
      createdReservation={reservationStorageData?.createdReservation}
    />
  )
}

const ReservationThankyou: React.FC<IProps> = props => {
  Hooks.SetDocumentTitle(props.post.name)
  const [showRefundInfoModal, setShowRefundInfoModal] = React.useState(false)

  React.useEffect(() => {
    const transactionId = props.createdReservation?.no
    if (!transactionId) {
      return
    }

    const lastTransactionId = getReservationCompletedTransactionId()
    if (lastTransactionId === transactionId) {
      return
    }

    const price = utilService.formatPriceToNumber(props.createdReservation?.charge_price ?? 0)
    const formattedGuests = {
      adults: props.guests?.adults ?? 0,
      children: props.guests?.children ?? 0,
      infants: props.guests?.infants ?? 0,
    }
    const guestCount = formattedGuests.adults + formattedGuests.children + formattedGuests.infants

    pushDataLayer({
      event: 'reservation_completed',
      userId: props.guest?.id || null,
      ecommerce: {
        transaction_id: transactionId,
        coupon: props.createdReservation?.coupon_name,
        currency: 'JPY',
        value: price,
        items: [
          {
            item_name: props.createdReservation?.post?.name,
            item_id: props.createdReservation?.post?.id,
            price,
            quantity: guestCount,
          },
        ],
      },
    })

    setReservationCompletedTransactionId(transactionId)
  }, [props.createdReservation, props.guest?.id, props.guests])

  const getParticipantTypePrice = (type: string): number => {
    const adultPrice =
      props.memberPost.post_prices.find(
        ({ participant_type, pricing_type }) =>
          participant_type === 'adult' || pricing_type === 'private'
      )?.price ?? 0

    const item = props.memberPost?.post_prices.find(
      ({ participant_type }) => participant_type === type
    )

    return item ? item?.price : adultPrice
  }

  const getTotalParticipantTypePrice = (quantity: number = 0, price: number) => {
    if (quantity === 0) {
      return 0
    }
    const total = quantity * price

    if (isNaN(total)) {
      return ''
    }

    return total
  }

  return (
    <FullScreenReservationLayout
      headerContent={
        <ReservationFullScreenModalHeaderContent
          type={ReservationModalType.Confirm}
          closeHandle={() => {
            clearReservationStorageAndInfo()
            setTimeout(() => {
              window.location.href = `/posts/${props.post.slug}`
            }, 300)
          }}
        />
      }
    >
      <ReservationResultWrapper>
        {showRefundInfoModal && (
          <RefundInfoModal
            cancelPolicy={props.cancelPolicy}
            onClose={() => setShowRefundInfoModal(false)}
          />
        )}

        <div className="ReservationResultInner completed">
          <div className="colLeft">
            <ReservationResult
              memberPost={props.memberPost}
              guests={props.guests}
              post={props.post}
              reservation={props.reservation}
              createdReservation={props.createdReservation}
            />
          </div>
          <div className="colRight">
            <ReservationDetailInfo
              post={props.post}
              memberPost={props.memberPost}
              isPostInFamily={props.post.family_owner}
              cancelPolicy={props.cancelPolicy}
              chargePrice={props.chargePrice}
              guests={props.guests}
              hasCoupon={props.hasCoupon}
              createdReservation={props.createdReservation}
              getParticipantTypePrice={getParticipantTypePrice}
              getTotalParticipantTypePrice={getTotalParticipantTypePrice}
              setShowRefundInfoModal={setShowRefundInfoModal}
            />
          </div>
        </div>
        <RelatedPosts post={props.post} user={props.guest} />
      </ReservationResultWrapper>
    </FullScreenReservationLayout>
  )
}
const S: { [key: string]: AnyStyledComponent } = {}

S.Reservation = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  padding-bottom: 12px;
  margin: 0;
  @media screen and (max-width: ${constants.BREAKPOINT_TABLET_MOBILE}px) {
    gap: 1.6rem;
  }
  .back,
  .title {
    font-size: 16px;
  }
  .date {
    display: inline-flex;
    flex-direction: column;
    gap: 12px;
  }
  .right {
    display: flex;
    justify-content: flex-end;
    flex-wrap: wrap;
  }
  .onRequestMessage {
    display: flex;
    flex-direction: column;
    gap: 0.8rem;
  }
  .boxItem > div:last-child {
    @media screen and (max-width: ${constants.BREAKPOINT_TABLET_MOBILE}px) {
      padding: 1.2rem 0;
    }
  }
`
const ReservationResultWrapper = styled.div`
  width: 100%;
  .ReservationResultInner {
    display: flex;
    justify-content: center;
    gap: 8rem;
    width: 100%;
    height: max-content;
    @media screen and (max-width: ${constants.BREAKPOINT_TABLET_LARGE}px) {
      flex-direction: column-reverse;
      gap: 4rem;
      &.completed {
        flex-direction: column;
        gap: 0;
        .colLeft {
          padding: 3.2rem 0 2.4rem;
        }
        .colRight {
          padding: 0 0 4rem;
        }
      }
    }
    @media screen and (max-width: ${constants.BREAKPOINT_TABLET_MOBILE}px) {
      gap: 2.4rem;
    }
  }
  .colLeft {
    width: 56rem;
    padding: 4.8rem 0 8rem;
    // max-height: calc(100vh - 160px);
    // overflow-y: auto;

    @media screen and (max-width: ${constants.BREAKPOINT_TABLET_LARGE}px) {
      width: 100%;
      max-width: 56rem;
      padding: 0 0 4rem;
      margin: 0 auto;
      // max-height: unset;
    }
  }

  .colRight {
    width: 48rem;
    min-height: 100%;
    padding: 4rem 0 6.4rem;
    @media screen and (max-width: ${constants.BREAKPOINT_TABLET_LARGE}px) {
      width: 100%;
      max-width: 56rem;
      padding: 2.4rem 0 0;
      margin: 0 auto;
    }
    @media screen and (max-width: ${constants.BREAKPOINT_TABLET_MOBILE}px) {
      padding: 1.6rem 0 0;
    }
  }

  .ReservationResult_Post {
    display: flex;
    align-items: center;
    @media (max-width: ${constants.BREAKPOINT_TABLET_MOBILE}px) {
      display: block;
    }
  }
`

export default WrapReservationThankyou
